// src/utils/utils_recipients.ts
//
// Règles communes aux destinataires d'un envoi, quel que soit leur canal.
// Email et WhatsApp partagent une seule zone de saisie : c'est ici qu'on décide,
// à la lecture, de quel canal relève ce que l'opérateur vient de taper.

import { Recipient       ,
         ContactChannel  }  from '../dataTypes/adminManagment/types_addressBook';

import { isValidWhatsappNumber }  from './utils_whatsapp';

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * Canal d'une saisie libre.
 *
 * L'arobase tranche : elle n'apparaît dans aucun numéro de téléphone, et aucune
 * adresse email ne s'en passe. Tout le reste est traité comme un numéro — y
 * compris ce qui n'en est pas un, qui deviendra une puce invalide plutôt que
 * d'être silencieusement rangé dans le mauvais canal.
 */
export const channelOf = (raw: string): ContactChannel =>
  raw.includes('@') ? 'email' : 'whatsapp';

/** Le destinataire correspondant à une saisie libre — sans nom, il n'y en a pas. */
export const parseRecipient = (raw: string): Recipient => {

  const value = raw.trim();

  return { channel: channelOf(value), value };
};

/** Le destinataire est-il expédiable ? */
export const isValidRecipient = (recipient: Recipient): boolean =>
  recipient.channel === 'whatsapp'
    ? isValidWhatsappNumber(recipient.value)
    : isValidEmail(recipient.value);

// ── Couleurs de puce ────────────────────────────────────────────────────────
//
// La couleur est le premier signal qui distingue un envoi mail d'un envoi
// WhatsApp dans une zone qui mélange les deux. Elle ne peut pas être le seul :
// une puce porte aussi son pictogramme, sans quoi la distinction disparaît pour
// qui ne perçoit pas ces deux teintes.

export const RECIPIENT_COLORS: Record<ContactChannel, { background: string; color: string }> = {
  email    : { background: '#6632FF' , color: '#FFFFFF' },  // violet de l'application
  // Vert WhatsApp « foncé » : le vert clair de la marque (#25D366) ne tient pas
  // le contraste sous un texte blanc.
  whatsapp : { background: '#128C7E' , color: '#FFFFFF' },
};

/**
 * Pictogramme de canal, doublure de la couleur : enveloppe Lucide pour le mail,
 * logo officiel de WhatsApp pour l'autre. Ce sont des noms d'icônes du composant
 * Icon — `Mail` vient du registre Lucide, `whatsapp` du registre SVG maison — et
 * non des emojis, qui sont multicolores par nature et ne prendraient pas la
 * teinte de leur canal.
 */
export const RECIPIENT_ICONS: Record<ContactChannel, string> = {
  email    : 'Mail'     ,
  whatsapp : 'whatsapp' ,
};

/**
 * Teinte du pictogramme employé SEUL, sans pastille derrière lui (carnet
 * d'adresses). Le vert de marque de WhatsApp convient ici, alors qu'il ne tient
 * pas le contraste sous le texte blanc d'une puce — d'où deux verts distincts.
 */
export const RECIPIENT_ICON_COLORS: Record<ContactChannel, string> = {
  email    : '#6632FF' ,
  whatsapp : '#25D366' ,
};

export const RECIPIENT_CHANNEL_LABELS: Record<ContactChannel, string> = {
  email    : 'Email'    ,
  whatsapp : 'WhatsApp' ,
};
